import type { SearchHit } from "@shared/projectSearch";

/**
 * What the reader last opened from the command bar, so an empty field starts
 * with the record they are most likely to want again. Kept on the device by
 * the same three rules as the project list's copy (lib/projectsSnapshot.ts):
 * drawn, never read as data (a row opens the live record); one account and
 * one project per list; only what the row and its preview draw (no email or
 * note text); gone at sign-out
 * (AuthContext calls `forgetCommandRecents`).
 */

const PREFIX = "salefish:cmd-recents:v1:";
const MAX = 6;

export type RecentHit = Pick<SearchHit, "kind" | "id" | "title" | "subtitle" | "via" | "contactId" | "openTab" | "saleId" | "status" | "facts">;

const key = (userId: string, projectId: string) => `${PREFIX}${userId}:${projectId}`;

export function readCommandRecents(userId: string | null | undefined, projectId: string | null | undefined): RecentHit[] {
  if (!userId || !projectId) return [];
  try {
    const raw = window.localStorage.getItem(key(userId, projectId));
    const list = raw ? (JSON.parse(raw) as RecentHit[]) : [];
    return Array.isArray(list) ? list.filter((h) => h && typeof h.id === "string" && typeof h.kind === "string").slice(0, MAX) : [];
  } catch {
    return [];
  }
}

export function rememberCommandRecent(userId: string | null | undefined, projectId: string | null | undefined, hit: SearchHit): void {
  if (!userId || !projectId) return;
  const entry: RecentHit = {
    kind: hit.kind, id: hit.id, title: hit.title, subtitle: hit.subtitle ?? null, via: hit.via ?? null,
    contactId: hit.contactId ?? null, openTab: hit.openTab ?? null, saleId: hit.saleId ?? null, status: hit.status ?? null,
    // The few facts its row and preview draw (a phone, a price), never a snippet of an email or a note.
    facts: (hit.facts ?? []).slice(0, 8),
  };
  try {
    const rest = readCommandRecents(userId, projectId).filter((h) => !(h.kind === hit.kind && h.id === hit.id));
    window.localStorage.setItem(key(userId, projectId), JSON.stringify([entry, ...rest].slice(0, MAX)));
  } catch {
    /* storage refused (private window, full): the bar works without it */
  }
}

/** Sign-out: every account's lists on this device. */
export function forgetCommandRecents(): void {
  try {
    for (let i = window.localStorage.length - 1; i >= 0; i--) {
      const k = window.localStorage.key(i);
      if (k?.startsWith(PREFIX)) window.localStorage.removeItem(k);
    }
  } catch {
    /* nothing kept */
  }
}
